import { createContext, useContext, useEffect, useState } from 'react';

import { createDxfViewerSession } from './session/createDxfViewerSession';
import type { DxfViewerSession } from './session/types';

const DxfViewerContext = createContext<DxfViewerSession | null>(null);
DxfViewerContext.displayName = 'DxfViewerContext';

const DxfMappingSavedContext = createContext<(() => void) | undefined>(
  undefined,
);
DxfMappingSavedContext.displayName = 'DxfMappingSavedContext';

/**
 * Owns the page's DXF viewer session.
 *
 * Consumers read controller lenses through `useReadableStore` (or helpers like
 * `useDxfLoad` / `useSessionModel`), so they re-render only on the slice they
 * asked for — never on pan or zoom.
 *
 * Mount once per viewer page, alongside `WebViewerContextProvider`. Doing so on a
 * page whose file is not a DXF costs nothing: a session holds no DOM and no WebGL
 * until `attachSurface` is called, and reports `load.status: 'idle'` until then.
 *
 * `onMappingSaved` lets the page refresh what a mapping changes: saving one
 * re-measures the part, and so its dimensions and pricing.
 */
export const DxfViewerProvider = ({
  children,
  onMappingSaved,
}: {
  children: React.ReactNode;
  onMappingSaved?: () => void;
}) => {
  const [session] = useState(createDxfViewerSession);

  // Cleanup the session when the component unmounts.
  useEffect(() => () => session.dispose(), [session]);

  return (
    <DxfViewerContext.Provider value={session}>
      <DxfMappingSavedContext.Provider value={onMappingSaved}>
        {children}
      </DxfMappingSavedContext.Provider>
    </DxfViewerContext.Provider>
  );
};
DxfViewerProvider.displayName = 'DxfViewerProvider';

/** The page's reaction to a saved mapping, if it has one. */
export const useOnDxfMappingSaved = (): (() => void) | undefined =>
  useContext(DxfMappingSavedContext);

export const useDxfViewerSession = (): DxfViewerSession => {
  const session = useContext(DxfViewerContext);
  if (!session) {
    throw new Error(
      'useDxfViewerSession must be used within a DxfViewerProvider',
    );
  }
  return session;
};
